<template>
  <ProductListLayout
    :results-text="documentCountInformation"
    :show-filter="true"
    :is-loading="store.loading"
    :content-heading-id="
      $textDictionary['documentCenterProductList.list.contentHeadingId']
    "
    :content-heading="
      $textDictionary['documentCenterProductList.list.contentHeading']
    "
    :filters-skip-btn-text="
      $textDictionary[
        'documentCenterProductList.filter.filtersSkipBtnText.label'
      ]
    "
    :filters-open-btn-text="
      $textDictionary[
        'documentCenterProductList.filter.filtersOpenBtnText.label'
      ]
    "
    :filters-modal-title="
      $textDictionary[
        'documentCenterProductList.filterModal.filtersModalTitle.label'
      ]
    "
    :filters-modal-close-icon-text="
      $textDictionary[
        'documentCenterProductList.filterModal.filtersModalCloseIconText.label'
      ]
    "
    :filters-modal-apply-btn-text="
      $textDictionary[
        'documentCenterProductList.filterModal.filtersModalApplyBtnText.label'
      ]
    "
  >
    <template #filter>
      <DownloadsDocumentsDocumentCenterFilters
        v-if="store.downloads && store.downloads?.filters.length !== 0"
        :filters="visibleFilters"
      />
    </template>
    <template #filterTags>
      <TagList
        :tags="formattedFilterFacets"
        :is-small="true"
        style-type="secondary"
        @close="removeTag"
      />
    </template>
    <template #list>
      <DocumentCenterList :sorted-criteria="sortedCriteria" />
    </template>
  </ProductListLayout>
</template>

<script setup lang="ts">
import { useDownloadStore } from '~/stores/downloadStore/downloadStore';
import { ProductListLayout, TagList } from '@hypercodestudio/basler-components';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import type { TagInterface } from '@hypercodestudio/basler-components/dist/components/elements/taglist/TagList.vue';
import { useFilterValueLabel } from '~/stores/downloadStore/useFilterValueLabel';
import type { FacetModel } from '~/lib/DownloadService/model';
import DocumentCenterList from '~/components/downloads/documents/DocumentCenterList.vue';

const router = useRouter();
const route = useRoute();
const store = useDownloadStore();
const { $textDictionary } = useNuxtApp();
const htmlDictionary = useHTMLDictionary(['downloadCenter.totalCount.label']);

interface Props {
  sortedCriteria?: string[];
}

defineProps<Props>();

watch(
  () => route.query,
  () => store.updateFiltersFromRoute(route.query),
  { immediate: true }
);

watch(
  () => store.filters,
  (newFilters, oldFilters) => {
    if (
      (store.downloads == null && oldFilters == null) || // initial fetch
      // as we change the route for a download detail
      // modal, this component is re-mounted. Due to `immediate` this
      // watcher will trigger with `oldFilters=undefined` which we ignore
      // if we already have downloads.
      (oldFilters != null &&
        JSON.stringify(oldFilters) !== JSON.stringify(newFilters))
    ) {
      store.fetchDownloads();
    }
  },
  { immediate: true, deep: false }
);

watch(
  () => route.params,
  () => store.handleRouteChange(route.params),
  {
    immediate: true
  }
);

function removeTag(tag: TagInterface) {
  router.replace({
    query: tag.link.uri.query,
    replace: true
  });
}

const filtersToDisplay = computed<string[] | undefined>(() => {
  if (store.context === 'pdp') {
    return ['downloadCategory.data', 'medialink.locale'];
  }
});

const documentCountInformation = computed(() => {
  let maxCount = 0;
  let count = 0;
  let start = 1;

  if (store.downloads?.results) {
    for (const key in store.downloads.results) {
      maxCount = maxCount + store.downloads.results[key].maxCount;
    }
  }
  if (store.downloads?.results) {
    for (const key in store.downloads.results) {
      count = count + store.downloads.results[key].hits.length;
    }
  }

  if (
    store.downloads?.results &&
    Object.keys(store.downloads?.results as any).length === 0
  ) {
    start = 0;
  }
  return (
    htmlDictionary.value['downloadCenter.totalCount.label']
      ?.replace('{start}', start.toString() ?? 1)
      ?.replace('{count}', count.toString() ?? 0)
      ?.replace('{total}', maxCount.toString())
      .replaceAll('<p>', '')
      .replaceAll('</p>', '') ?? ''
  );
});

const visibleFilters = computed<FacetModel[]>(() => {
  const filters = store.downloads?.filters ?? [];

  if (!filtersToDisplay.value) {
    return filters;
  }

  return filters.filter((filter) =>
    filtersToDisplay.value?.includes(filter.name)
  );
});

const formattedFilterFacets = computed<TagInterface[]>(() => {
  if (!store.hasResults) {
    return [];
  }

  const filters = store.validFilters;
  const facets: TagInterface[] = [];

  for (const filterName in filters) {
    const filterLabelPrefix = 'productDocumentDownloadTab.';

    const filterLabel = computed(
      () =>
        $textDictionary.value[filterLabelPrefix + filterName + '.label'] ??
        filterName
    );
    for (const filterValue of filters[filterName]) {
      facets.push({
        text:
          filterLabel.value +
          ': ' +
          useFilterValueLabel(filterName, filterValue),
        showCloseIcon: true,
        element: 'button',
        link: {
          uri: store.createNewUrlParamsFromFilter(filterName, filterValue)
        } satisfies HyperlinkInterface
      });
    }
  }

  return facets;
});
</script>
